import { useId, useState } from "react";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import {
  getAccountIndicationPdf,
  type AccountAdvanceLimits,
  type AccountPreapproval,
} from "@/lib/brokerAccounts";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { buttonClass } from "@/components/buttonStyles";
import { statusBadgeClass } from "@/lib/listFormat";
import {
  programEnrollment,
  programFigures,
  programInfo,
} from "@/lib/programEnrollment";
import type { ReadAdvanceRequirements } from "@/lib/signalAdvanceEvidence";
import { ProgramInfoPopover } from "@/components/ProgramInfoPopover";
import { saveFile } from "@/lib/downloadFile";
import { messageFor } from "@/lib/errors";
import { ProgramUtilization } from "@/components/ProgramUtilization";
import { PreapprovalApplicationStatus } from "@/components/PreapprovalApplicationStatus";
import type { readPreapprovalApplication } from "@/lib/preapprovalApplication";
import { ActiveBondsPanel } from "@/components/surety-account/ActiveBondsPanel";
import { SendPrequalificationDialog } from "@/components/SendPrequalificationDialog";
import {
  PayApplicationDialog,
  type FiledPayApplication,
} from "@/components/surety-account/PayApplicationDialog";
import { estimatedReleaseLine } from "@/lib/payApplication";
import type { CapacityRequestView } from "@/lib/capacityRequest";
import { WELL } from "@/base";

// BRK-S139 — the standing surety PROGRAM, on the account profile. Since
// BRK-S401 it is the wireframe's "Current program enrollment" block, drawn on
// both the Company profile and Surety program tabs, and since BRK-S501 it
// draws the October wireframe's three states exactly: a title row (Not
// enrolled / Signal Express / Signal Advance, its pill and "Program approved")
// beside the two program buttons, each with an info popover; then, for a
// program in force, the figures, the utilization bar, the Active bonds box and
// the three program buttons (Send pre-qualification, Show progress, Print
// indication letter — BRK-S403). Not enrolled, the block is the title row and
// the two enrol buttons and nothing else. `programEnrollment`
// (src/lib/programEnrollment.ts) is what reads the state.
//
// A pre-approval is a program the INSURED holds, not a fact about one bond
// order: a single-bond limit, an aggregate program limit, and a rate. So it
// belongs on the account, and this is the surface a broker quotes from — which
// is why the figures are rendered the way Signal granted them and the letter is
// one click away.
//
// THREE STATES, and the two that are not "approved" are different facts:
//
//   • `approved`     — the three figures, the rate, when it was decided, and the
//     Program Indication letter.
//   • `not_granted`  — the insured has been evaluated and holds no program.
//   • `none`         — nobody has evaluated them. There is something to DO about
//     this one, where a refusal is an answer the broker already has, so it is
//     the only state that leads with the call to action.
//
// NOTHING HERE EXPLAINS A DECISION, and nothing here may. There is no score, no
// reason, no tier, no surety name, no A.M. Best rating and no Treasury status —
// not "not read", but not published, because Signal cannot state them to a
// broker (PMS-E015). `not_granted` collapses a refusal and a WITHDRAWAL
// indistinguishably, on purpose, and the word "declined" never appears:
// `not_granted` is not a decline. `test/lib/preapprovalDiscretion.test.ts` is
// what keeps that true of this repo rather than of this comment.

/** What to call the letter if PMS sends no `Content-Disposition` — a readable
 *  word, deliberately not the account id. */
const LETTER_FALLBACK = "program-indication.pdf";

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-caption font-medium uppercase tracking-wide text-brk-dim">
        {label}
      </dt>
      <dd className="break-words text-heading text-brk-text">{value}</dd>
    </div>
  );
}

/** A program's row: an Enroll action or a chip, then its info popover. */
function ProgramRow({
  children,
  info,
}: {
  children: React.ReactNode;
  info: React.ReactNode;
}) {
  return (
    <li className="flex min-w-0 flex-wrap items-center gap-2">
      {children}
      {info}
    </li>
  );
}

/** The program buttons' looks, the wireframe's: a done program is a grey
 *  "✓ … · Enrolled", Express's enrol a green outline, Advance's enrol the
 *  accent, Advance held a lilac "Signal Advance · Enrolled". */
const PROGRAM_BUTTON =
  "inline-flex h-9 items-center justify-center gap-1.5 rounded-md px-4 text-body font-medium [@media(pointer:coarse)]:min-h-11";
const DONE = `${PROGRAM_BUTTON} bg-brk-bg text-brk-dim`;
const ENROLL_EXPRESS = `${PROGRAM_BUTTON} border border-green-600 text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-green-500 dark:text-green-400 dark:hover:bg-green-400/10`;
const ADVANCE_ENROLLED = `${PROGRAM_BUTTON} bg-violet-100 text-violet-700 dark:bg-violet-400/15 dark:text-violet-300`;

function Done({ children }: { children: React.ReactNode }) {
  return (
    <span className={DONE}>
      <Check size={16} aria-hidden />
      {children}
    </span>
  );
}

/**
 * BRK-S160 — the standalone pre-approval, now labelled "Enroll in Signal
 * Express" (BRK-S401): it is the same flow, under the name the program has.
 *
 * Until BRK-S160 the ONLY way to a program pre-qualification was starting a
 * bid bond and walking its whole intake. The standalone flow is a subproduct
 * of its own, so the CTA needs the SUBPRODUCT ID to link to — which arrives as
 * a PROP, resolved by the page from the catalog by
 * `PROGRAM_PREAPPROVAL_SUBPRODUCT_KEY`. Null/absent is an ordinary state and
 * not an error: a catalog with no such subproduct is a PMS that has not been
 * given the data yet, and the block falls back to "Start a bond request" so
 * nothing is ever a dead link.
 */
export function SuretyProgramBlock({
  accountId,
  preapproval,
  preapprovalSubproductId,
  application = null,
  advance = null,
  capacityRequest,
  onCapacityChanged,
  advanceLimits,
}: {
  accountId: string;
  preapproval: AccountPreapproval;
  /** The program record's Advance limits (BRK-S501, PMS-S915): the Advance
   *  popover's "up to" figures. The account page serves them — null while
   *  Signal has not saved its program terms; an order's own block has no
   *  program record. */
  advanceLimits?: AccountAdvanceLimits | null;
  /** The catalog subproduct that runs the standalone flow, when one exists. */
  preapprovalSubproductId?: string | null;
  /**
   * The insured's current pre-approval application (BRK-S324, PMS-S719),
   * already read. While it is open or refused it REPLACES "Enroll in Signal
   * Express" with its own status and action; `under_review` is the block's
   * "Application in review" state. Absent/null: the panel reads as before.
   */
  application?: ReturnType<typeof readPreapprovalApplication>;
  /** The Signal Advance evidence (BRK-S330), already read. Both requirements
   *  met on an Express program reads "Signal Advance · In review". */
  advance?: ReadAdvanceRequirements | null;
  /** The latest capacity request (BRK-S329). `undefined` renders no capacity
   *  line at all — a surface that has not read it says nothing about it. */
  capacityRequest?: CapacityRequestView | null;
  /** Re-read the account after a change to what holds capacity (BRK-S402). */
  onCapacityChanged?: () => void;
}) {
  const { authedBlob } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The program buttons' dialogs (BRK-S403): the pre-qualification letter
  // (BRK-S405) and the pay application (BRK-S404).
  const [dialog, setDialog] = useState<"prequal" | "payApp" | null>(null);
  // What PMS's 2xx to a filed pay application said, worded as the bar does.
  const [notice, setNotice] = useState<string | null>(null);
  // Generated, not fixed: the account profile draws this block on two tabs
  // (BRK-S398), and two sections must not share one heading id.
  const headingId = useId();

  function onPayAppFiled(filed: FiledPayApplication) {
    setDialog(null);
    setNotice(
      [
        `${filed.orderNumber}: pay application #${String(filed.applicationNumber)} is with your underwriter.`,
        estimatedReleaseLine(filed.estimatedRelease),
      ]
        .filter((line) => line !== null)
        .join(" "),
    );
    // The page re-reads the account, so the open request is PMS's answer.
    onCapacityChanged?.();
  }

  async function printLetter() {
    setBusy(true);
    setError(null);
    try {
      saveFile(
        await getAccountIndicationPdf(authedBlob, accountId),
        LETTER_FALLBACK,
      );
    } catch (err) {
      // A handled line, never a throw (NN #7). A 404 here means the mint has
      // not landed yet — best-effort upstream and retried — so the sentence PMS
      // sends is the one that fits.
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  const enrollment = programEnrollment({ preapproval, application, advance });
  const terms = preapproval.status === "approved" ? preapproval.terms : null;
  const enrolled = enrollment.state !== "none";
  // An application to show in place of "Enroll in Signal Express": anything
  // but `approved`, which the standing program already says (BRK-S324 — no
  // second application while one is open, Re-apply after a refusal).
  const current =
    !enrolled && application && application.status !== "approved"
      ? application
      : null;
  const enrollExpressHref = preapprovalSubproductId
    ? `/surety/submissions/new?subproduct=${encodeURIComponent(
        preapprovalSubproductId,
      )}&account=${encodeURIComponent(accountId)}`
    : null;
  const pillTone = enrollment.tone === "neutral" ? "draft" : enrollment.tone;
  // The account page gives the block the capacity request (it read it); an
  // order's own program block does not. Active bonds and the two program
  // actions are the account's, as the per-bond bar is (BRK-S402).
  const onAccount = capacityRequest !== undefined;

  return (
    <section
      aria-labelledby={headingId}
      className={`flex min-w-0 flex-col gap-5 ${WELL}`}
    >
      <h2 id={headingId} className="text-heading text-brk-text">
        Current program enrollment
      </h2>

      {/* The title row: WHERE the insured stands, beside the two program
          buttons. Stacked at 375, side by side from `md:`. NEUTRAL when no
          program is in force: `not_granted` is not a decline and must not
          read as one — it says "Not enrolled", exactly as `none` does. */}
      <div className="flex min-w-0 flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-subheading text-brk-text">{enrollment.title}</p>
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-caption font-medium ${statusBadgeClass(pillTone)}`}
            >
              {enrollment.pill}
            </span>
          </div>
          <p className="text-meta text-brk-dim">
            Program approved:{" "}
            {enrolled && preapproval.evaluatedAt
              ? formatBondDate(preapproval.evaluatedAt)
              : "—"}
          </p>
          {/* When the program runs out (BRK-S277). Null whenever no program
              is in force, and never a past date (see
              `AccountPreapproval.expiresAt`), so it renders without a clock. */}
          {enrolled && preapproval.expiresAt && (
            <p className="text-meta text-brk-dim">
              Expires {formatBondDate(preapproval.expiresAt)}
            </p>
          )}
        </div>

        <ul
          aria-label="Programs"
          className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start"
        >
          <ProgramRow
            info={
              <ProgramInfoPopover info={programInfo("express", preapproval)} />
            }
          >
            {enrollment.expressChip ? (
              <Done>{enrollment.expressChip}</Done>
            ) : current ? (
              <PreapprovalApplicationStatus
                accountId={accountId}
                application={current}
              />
            ) : enrollExpressHref ? (
              <Link to={enrollExpressHref} className={ENROLL_EXPRESS}>
                Enroll in Signal Express
              </Link>
            ) : (
              // The catalog read is still in flight: the way in is not known
              // yet, so the button waits rather than guessing a route.
              <button type="button" disabled className={ENROLL_EXPRESS}>
                Enroll in Signal Express
              </button>
            )}
          </ProgramRow>
          {/* THE SIGNAL ADVANCE ENTRY POINT (BRK-S161): a LINK to the
              account's Signal Advance application section (BRK-S407,
              `#advance-app` on the Surety program tab), where the broker
              sends the client their links. */}
          <ProgramRow
            info={
              <ProgramInfoPopover
                info={programInfo("advance", preapproval, advanceLimits)}
              />
            }
          >
            {enrollment.canEnrollAdvance ? (
              <Link
                to={`/surety/accounts/${encodeURIComponent(accountId)}?tab=program#advance-app`}
                className={buttonClass()}
              >
                Enroll in Signal Advance →
              </Link>
            ) : enrollment.state === "advance" ? (
              <span className={ADVANCE_ENROLLED}>{enrollment.advanceChip}</span>
            ) : (
              <span className={DONE}>{enrollment.advanceChip}</span>
            )}
          </ProgramRow>
        </ul>
      </div>

      {enrolled && terms && (
        <>
          {/* The figures the way Signal granted them: cents kept
              (`formatPreapprovalMoney`) and the rate per $1,000 shown as a
              percent — `ratePer1000 / 10`, the one conversion. "Flat rate" is
              Signal's own wording for a rate that does not tier with the bond
              amount. Stacked at 375, three across from `sm:`. */}
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {programFigures(terms).map((f) => (
              <Figure key={f.label} label={f.label} value={f.value} />
            ))}
          </dl>

          {/* Aggregate utilized (BRK-S186). Only for a NON-NULL block: absent
              (a PMS that predates PMS-S493) and null (no current program) both
              render nothing — never "$0". Per bond, with actions (BRK-S402),
              where the surface read the capacity request: the account page.
              An order's own page keeps the one-fill bar. */}
          {preapproval.capacity && (
            <ProgramUtilization
              accountId={accountId}
              capacity={preapproval.capacity}
              capacityRequest={capacityRequest ?? null}
              perBond={capacityRequest !== undefined}
              onChanged={onCapacityChanged}
            />
          )}

          {/* The insured's active bonds (BRK-S403), beneath the bar. */}
          {onAccount && <ActiveBondsPanel accountId={accountId} />}

          <div className="flex flex-col gap-2">
            {/* One row of program actions; they wrap at 375. An order's
                block with no letter yet has none, and draws no empty row. */}
            {(onAccount || preapproval.indicationAvailable) && (
              <div
                role="group"
                aria-label="Program actions"
                className="flex flex-wrap gap-2"
              >
                {onAccount && (
                  <>
                    <button
                      type="button"
                      onClick={() => setDialog("prequal")}
                      className={buttonClass()}
                    >
                      Send pre-qualification
                    </button>
                    {/* Opened with no bond preselected: the broker picks the
                      P&P bond in the dialog. */}
                    <button
                      type="button"
                      onClick={() => setDialog("payApp")}
                      className={buttonClass({ variant: "secondary" })}
                    >
                      Show progress
                    </button>
                  </>
                )}
                {/* Offered only when the LETTER exists — its own axis, not
                  derivable from the status (see `getAccountIndicationPdf`). */}
                {preapproval.indicationAvailable && (
                  <button
                    type="button"
                    onClick={printLetter}
                    disabled={busy}
                    className={buttonClass({ variant: "secondary" })}
                  >
                    {busy ? "Preparing…" : "Print indication letter"}
                  </button>
                )}
              </div>
            )}
            {!preapproval.indicationAvailable && (
              <p className="text-sm text-brk-dim">
                Your indication letter is still being prepared — it will appear
                here shortly.
              </p>
            )}
            {error && (
              <p role="alert" className="text-sm text-brk-danger">
                {error}
              </p>
            )}
            {notice && (
              <p role="status" className="text-sm text-brk-text">
                {notice}
              </p>
            )}
          </div>
          {onAccount && (
            <>
              <SendPrequalificationDialog
                accountId={accountId}
                open={dialog === "prequal"}
                onClose={() => setDialog(null)}
              />
              {/* No bond preselected: the broker picks the locked P&P in
                  the dialog. */}
              <PayApplicationDialog
                accountId={accountId}
                orderId={null}
                open={dialog === "payApp"}
                onClose={() => setDialog(null)}
                onFiled={onPayAppFiled}
              />
            </>
          )}
        </>
      )}
    </section>
  );
}
